/**
 * Canonical AR entry contract (Prompt 3B).
 * Contract: docs/doar-core/DOARSPACE_UNIVERSAL_WORKSPACE_CONTRACT.md #5.
 *
 * There is exactly ONE canonical DoARSpace AR/spatial route
 * (/d/:doarId/ar), owned by SpatialSession + WebXRSpatialRuntime. Every
 * workspace surface (Universal3DWorkspace, WorldApp, WorldInteractionDock)
 * launches AR by resolving a canonical DoAR id and navigating to that route
 * - none of them decide WebXR vs Quick Look vs Scene Viewer themselves; that
 * platform/device decision lives inside SpatialSession's own boundary
 * (resolveSpatialArStrategy below is used FROM there, not by workspace
 * callers). Public Reality reuses the same pure strategy so shopper and
 * workspace entry points cannot drift into different platform choices.
 *
 * Deliberately lightweight: this module must never import SpatialSession,
 * WebXRSpatialRuntime, Three.js, model-viewer, or Cesium, so that importing
 * it (e.g. from Universal3DWorkspace for the AR Output CTA) never pulls any
 * of those into the ordinary Studio/Earth bundle.
 */
import { isObjectId } from "../handoffContext";
import safeLocalReturnPath from '../../../utils/safeReturnPath';

export const AR_STRATEGY = Object.freeze({
  FIRST_PARTY_WEBXR: "first-party-webxr",
  IOS_QUICK_LOOK: "ios-quick-look",
  ANDROID_SCENE_VIEWER: "android-scene-viewer",
  UNSUPPORTED: "unsupported",
});

export function validatedWorkspaceReturn(value) {
  if (typeof value !== 'string' || !value.startsWith('/space/') || !safeLocalReturnPath(value)) return null;
  try {
    const parsed = new URL(value, 'https://workspace.invalid');
    if (parsed.origin !== 'https://workspace.invalid' || !/^\/space\/[a-zA-Z0-9_-]+\/?$/.test(parsed.pathname)) return null;
    return parsed.pathname + parsed.search + parsed.hash;
  } catch { return null; }
}

/**
 * The canonical DoARSpace spatial route. Never build an AR URL from a board
 * id, scene-object id, arbitrary experienceUrl, or Asset._id - only a
 * validated DoAR id.
 */
export function buildCanonicalArPath(doarId, { listing = null, revisionId = null, assetId = null, returnTo = null } = {}) {
  if (!isObjectId(doarId)) return null;
  if (!listing && ((revisionId !== null && !isObjectId(revisionId)) || (assetId !== null && !isObjectId(assetId)))) return null;
  const base = `/d/${doarId}/ar`;
  const params = new URLSearchParams();
  if (listing) params.set('listing', listing);
  if (!listing && isObjectId(revisionId)) params.set('revision', revisionId);
  if (!listing && isObjectId(assetId)) params.set('asset', assetId);
  const workspaceReturn = !listing && validatedWorkspaceReturn(returnTo);
  if (workspaceReturn) params.set('returnTo', workspaceReturn);
  const query = params.toString().replace(/\+/g, '%20');
  return query ? `${base}?${query}` : base;
}

/**
 * Canonical DoAR id rule (contract §4): never Asset._id. Preferred identity
 * is asset.doArObjectId. WorkspaceSession.activeDoarId may stand in only
 * when there is no competing selected Asset (i.e. no specific asset was
 * passed at all) - it must never be substituted for a DIFFERENT asset's own
 * missing id, since that would attach one object's AR launch to another
 * object's identity.
 */
export function resolveCanonicalArDoarId({ asset = null, sessionActiveDoarId = null } = {}) {
  if (asset) {
    return isObjectId(asset.doArObjectId) ? asset.doArObjectId : null;
  }
  return isObjectId(sessionActiveDoarId) ? sessionActiveDoarId : null;
}

export function nativeArCompatibilityMessage(scene) {
  if (scene?.nativeVersion !== 2 || scene.native?.android?.reason !== 'SCENE_DELIVERY_NOT_READY') return null;
  const assessment = scene.compatibility;
  const reasons = assessment?.reasons || [];
  const limits = [];
  if (reasons.includes('GLB_TOO_LARGE') && Number.isFinite(assessment.fileSizeBytes) && Number.isFinite(assessment.maxBytes)) {
    limits.push(`${(assessment.fileSizeBytes / 1024 ** 2).toFixed(1)} MiB (limit ${(assessment.maxBytes / 1024 ** 2).toFixed(0)} MiB)`);
  }
  if (reasons.includes('GLB_TRIANGLE_BUDGET_EXCEEDED') && Number.isFinite(assessment.triangleCount) && Number.isFinite(assessment.maxTriangles)) {
    limits.push(`${assessment.triangleCount.toLocaleString('en-US')} triangles (limit ${assessment.maxTriangles.toLocaleString('en-US')})`);
  }
  return limits.length
    ? `This saved scene exceeds the mobile AR limits: ${limits.join('; ')}. Use a smaller supported scene or an optimized source model. The original source and saved scene are preserved.`
    : 'This saved scene has not passed AR compatibility checks. Use a supported representation. The original source and saved scene are preserved.';
}

/**
 * What this session can actually deliver, per mode. A saved spatial scene
 * (session.scene) is authoritative: WebXR composes its visible nodes, while
 * Quick Look / Scene Viewer are offered only when the server proved a single
 * file shows the exact scene. Sessions without a saved scene keep the
 * single-representation preview.
 */
export function sceneDelivery(session) {
  const scene = session?.scene || null;
  if (!scene) {
    const glbUrl = session?.preview?.glbUrl || null, usdzUrl = session?.preview?.usdzUrl || null;
    return { scene: null, hasWebxrModel: Boolean(glbUrl), webxrFidelity: glbUrl ? 'exact' : 'unavailable',
      nativeGlbUrl: glbUrl, nativeUsdzUrl: usdzUrl, inlinePreviewUrl: glbUrl, notices: [] };
  }
  const notices = [];
  if (scene.fidelity?.webXR === 'partial') notices.push({ code: 'SCENE_PARTIAL', count: scene.missing?.length || 0, names: (scene.missing || []).map(row => row.name) });
  if (scene.fidelity?.ios !== 'exact' && scene.fidelity?.android !== 'exact') notices.push({ code: 'NATIVE_SCENE_UNSUPPORTED', reason: scene.native?.android?.reason || scene.native?.ios?.reason || null });
  if (session?.readiness?.scaleState === 'scene-provisional') notices.push({ code: 'SCENE_PROVISIONAL_SCALE' });
  const singleExact = scene.nodes?.length === 1 && scene.fidelity?.android === 'exact' && !scene.native?.android?.composite;
  return {
    scene,
    hasWebxrModel: scene.fidelity?.webXR === 'exact' || scene.fidelity?.webXR === 'partial',
    webxrFidelity: scene.fidelity?.webXR || 'unavailable',
    nativeGlbUrl: scene.native?.android?.status === 'exact' ? scene.native.android.url : null,
    nativeUsdzUrl: scene.native?.ios?.status === 'exact' ? scene.native.ios.url : null,
    // An inline single-model preview is truthful only for one unmodified node.
    inlinePreviewUrl: scene.nativeVersion===2&&scene.fidelity?.android==='exact'&&scene.native?.android?.composite ? scene.native.android.url : singleExact ? scene.nodes[0].url : null,
    notices,
  };
}

/**
 * Pure spatial-runtime decision boundary (contract §13/§31). Inputs are
 * booleans/platform flags only - no DOM, no navigator, no WebXR/model-viewer
 * API calls - so this is directly unit-testable and reusable from
 * SpatialSession without pulling this module into anything heavier.
 *
 * WebXR must win first whenever it is genuinely available for a first-party
 * spatial visual (a real GLB or a parametric/curtain scene) - never a native
 * intent/model-viewer AR session first. webxrSupported must come from
 * WebXRSpatialRuntime.supported() - this function never infers WebXR
 * support from user-agent alone.
 */
export function resolveSpatialArStrategy({
  webxrSupported = false,
  hasFirstPartySpatialVisual = false,
  hasGlb = false,
  hasUsdz = false,
  isApple = false,
  isAndroid = false,
} = {}) {
  if (webxrSupported && hasFirstPartySpatialVisual) return AR_STRATEGY.FIRST_PARTY_WEBXR;
  if (isApple && hasUsdz) return AR_STRATEGY.IOS_QUICK_LOOK;
  if (isAndroid && hasGlb) return AR_STRATEGY.ANDROID_SCENE_VIEWER;
  return AR_STRATEGY.UNSUPPORTED;
}
